/**
 * adminApi.js — client for admin analytics endpoints.
 * Uses the existing Axios instance (JWT auto-refresh).
 *
 * Each function returns the `data` shape from the API contract,
 * or an empty fallback if the backend is unavailable (404/5xx).
 * Fullstack-dev swaps the fallbacks once endpoints are live.
 */

import api from '../utils/api';
import { subDays, formatISO } from 'date-fns';

// ─── Default date range helpers ───────────────────────────────────────────────

function defaultFrom() {
  return formatISO(subDays(new Date(), 7), { representation: 'date' });
}

function defaultTo() {
  return formatISO(new Date(), { representation: 'date' });
}

function baseParams(overrides = {}) {
  return {
    from: defaultFrom(),
    to: defaultTo(),
    ...overrides,
  };
}

// ─── Empty fallbacks (used when backend is not yet live) ──────────────────────

const EMPTY_OVERVIEW = {
  kpis: { visits: 0, uniques: 0, sessions: 0, avgDwellMs: 0, bounceRate: 0, activeNow: 0 },
  trend: [],
  topPages: [],
  topReferrers: [],
  sources: { direct: 0, organic: 0, referral: 0, campaign: 0 },
};

const EMPTY_AUDIENCE = { rows: [], geo: [] };

const EMPTY_BEHAVIOR_PAGES = { rows: [] };

const EMPTY_BEHAVIOR_FLOW = { nodes: [], links: [] };

const EMPTY_ACQUISITION = { referrers: [], utm: [] };

const EMPTY_CONVERSIONS = {
  funnel: { id: '', name: '', steps: [] },
  revenuePaise: 0,
};

const EMPTY_REALTIME = {
  activeCount: 0,
  byPath: [],
  byCountry: [],
  recent: [],
};

const EMPTY_USERS = { rows: [], total: 0 };

const EMPTY_TIMELINE = { items: [] };

const EMPTY_EVENTS = { rows: [] };

// ─── Safe fetch wrapper ───────────────────────────────────────────────────────

async function safeFetch(fn, fallback) {
  try {
    const res = await fn();
    return res?.data?.data ?? fallback;
  } catch (err) {
    const status = err?.response?.status;
    // Return empty shape for any error (404 = not yet live, 5xx = backend down)
    if (!status || status >= 400) return fallback;
    return fallback;
  }
}

// ─── Endpoint functions ───────────────────────────────────────────────────────

/**
 * getOverview(params) — KPIs, trend, top pages, top referrers, sources.
 * @param {Object} params — { from, to, includeInternalRoles, includeBots }
 */
export async function getOverview(params = {}) {
  return safeFetch(
    () => api.get('/admin/analytics/overview', { params: baseParams(params) }),
    EMPTY_OVERVIEW,
  );
}

/**
 * getAudience(params) — dimension breakdown rows + geo array.
 * @param {Object} params — { from, to, dimension, newReturning, ... }
 */
export async function getAudience(params = {}) {
  return safeFetch(
    () => api.get('/admin/analytics/audience', { params: baseParams(params) }),
    EMPTY_AUDIENCE,
  );
}

/**
 * getBehaviorPages(params) — page performance table.
 * @param {Object} params — { from, to, limit }
 */
export async function getBehaviorPages(params = {}) {
  return safeFetch(
    () => api.get('/admin/analytics/behavior/pages', { params: baseParams({ limit: 50, ...params }) }),
    EMPTY_BEHAVIOR_PAGES,
  );
}

/**
 * getBehaviorFlow(params) — Sankey flow data.
 * @param {Object} params — { from, to, entry, depth }
 */
export async function getBehaviorFlow(params = {}) {
  return safeFetch(
    () => api.get('/admin/analytics/behavior/flow', {
      params: baseParams({ entry: '/', depth: 5, ...params }),
    }),
    EMPTY_BEHAVIOR_FLOW,
  );
}

/**
 * getAcquisition(params) — referrers + UTM table.
 * @param {Object} params — { from, to, ... }
 */
export async function getAcquisition(params = {}) {
  return safeFetch(
    () => api.get('/admin/analytics/acquisition', { params: baseParams(params) }),
    EMPTY_ACQUISITION,
  );
}

/**
 * getConversions(params) — funnel steps + revenue.
 * @param {Object} params — { from, to, funnel }
 */
export async function getConversions(params = {}) {
  return safeFetch(
    () => api.get('/admin/analytics/conversions', { params: baseParams(params) }),
    EMPTY_CONVERSIONS,
  );
}

/**
 * getRealtime() — active count, by-path, by-country, recent feed.
 * No date params — always current.
 */
export async function getRealtime() {
  return safeFetch(
    () => api.get('/admin/analytics/realtime'),
    EMPTY_REALTIME,
  );
}

/**
 * getUsers(params) — paginated user table.
 * @param {Object} params — { search, limit, offset }
 */
export async function getUsers(params = {}) {
  return safeFetch(
    () => api.get('/admin/analytics/users', {
      params: { limit: 50, offset: 0, ...params },
    }),
    EMPTY_USERS,
  );
}

/**
 * getUserTimeline(userId, params) — chronological event list for a user.
 * @param {string} userId
 * @param {Object} params — { from, to }
 */
export async function getUserTimeline(userId, params = {}) {
  return safeFetch(
    () => api.get(`/admin/analytics/users/${userId}/timeline`, {
      params: baseParams(params),
    }),
    EMPTY_TIMELINE,
  );
}

/**
 * getEvents(params) — raw event rows.
 * @param {Object} params — { name, limit }
 */
export async function getEvents(params = {}) {
  return safeFetch(
    () => api.get('/admin/analytics/events', {
      params: { limit: 100, ...params },
    }),
    EMPTY_EVENTS,
  );
}
